/**
 * Attach Sentry Replay after the page has loaded, instead of in `Sentry.init`.
 *
 * Replay (rrweb) is ~41 KB gz. Listed in `integrations`, it sat in the
 * pre-hydration root chunk; loaded here through `lib/sentryReplayIntegration`
 * it becomes an async chunk fetched once the page is idle.
 * `client.addIntegration` runs the integration's setup on the live client, and
 * Replay reads `replaysSessionSampleRate` / `replaysOnErrorSampleRate` from
 * the client options when it attaches, so the 100% sampling invariant is
 * unchanged (see `.claude/docs/web-analytics-full.md`).
 *
 * Not `Sentry.lazyLoadIntegration`: that injects a script from Sentry's CDN
 * (third-party origin, often ad-blocked). Not `import("@sentry/nextjs")`
 * either: importing the whole namespace keeps every export in the bundle.
 *
 * Trigger: `runAfterLoadIdle` (lib/afterLoadIdle.ts) — after `load` plus an
 * idle callback (2 s timeout), with a 10 s ceiling so a boot stuck before
 * `load` still records a replay (CUSTOMER-UI-3Z triage needs it).
 *
 * What is lost: activity and errors before the attach (~1–3 s) have no Sentry
 * replay. The Mixpanel recorder starts on the same schedule
 * (lib/mixpanelClient.ts), so neither replay covers that window; Sentry
 * breadcrumbs and the Mixpanel events (tracked from init) still do.
 *
 * Like `bootTelemetry.ts`, this module has no value import of `@sentry/nextjs`
 * (lib/ specs share one process and some mock that package), so the Sentry
 * function, the loader and the window are injected.
 *
 * The caller must gate on Sentry being enabled: unlike `Sentry.init`,
 * `addIntegration` does not check `enabled`, so an ungated call would start
 * rrweb recording on localhost / E2E / mock mode with nothing ever sent.
 */
import type * as SentryNamespace from "@sentry/nextjs";

import {
  AFTER_LOAD_CEILING_MS,
  AFTER_LOAD_IDLE_TIMEOUT_MS,
  type AfterLoadIdleWindow,
  runAfterLoadIdle,
} from "./afterLoadIdle";
import { noteSilentSignal } from "./noteSilentSignal";

export const SENTRY_REPLAY_OPTIONS = { minReplayDuration: 10_000 } as const;

/** `requestIdleCallback` timeout once `load` has fired. */
export const REPLAY_IDLE_TIMEOUT_MS = AFTER_LOAD_IDLE_TIMEOUT_MS;

/** Attach no later than this after install, even if `load` never fires. */
export const REPLAY_ATTACH_CEILING_MS = AFTER_LOAD_CEILING_MS;

type SentryIntegration = Parameters<typeof SentryNamespace.addIntegration>[0];

type ReplayIntegrationFactory = (options: {
  minReplayDuration: number;
}) => SentryIntegration;

/** The slice of `window` this module touches, injectable for tests. */
export type LazyReplayWindow = AfterLoadIdleWindow;

const loadReplayIntegrationChunk = (): Promise<ReplayIntegrationFactory> =>
  import("./sentryReplayIntegration").then((m) => m.replayIntegration);

export function installLazySentryReplay({
  addIntegration,
  loadReplayIntegration = loadReplayIntegrationChunk,
  win = window,
}: {
  addIntegration: (integration: SentryIntegration) => void;
  loadReplayIntegration?: () => Promise<ReplayIntegrationFactory>;
  win?: LazyReplayWindow;
}): () => void {
  let disposed = false;

  const attach = () => {
    loadReplayIntegration()
      .then((replayIntegration) => {
        if (disposed) return;
        addIntegration(replayIntegration({ ...SENTRY_REPLAY_OPTIONS }));
      })
      .catch((error: unknown) => {
        // Unhandled, a failed chunk load would reach the global rejection
        // listeners and the toast bridge. Observable, never an issue.
        noteSilentSignal({
          message: "[Boot] Sentry replay failed to load",
          signal: "sentry-replay-load-failed",
          extra: {
            errorName: error instanceof Error ? error.name : typeof error,
          },
        });
      });
  };

  const cancel = runAfterLoadIdle(attach, {
    win,
    idleTimeoutMs: REPLAY_IDLE_TIMEOUT_MS,
    ceilingMs: REPLAY_ATTACH_CEILING_MS,
  });

  return () => {
    disposed = true;
    cancel();
  };
}
